import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Withdrawal, WithdrawalWindow } from '../types';
import { api } from '../api';
import './Withdrawals.css';

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);

// TRON base58 address: starts with "T", 34 chars total
const TRX_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

const shortWallet = (addr: string | null) =>
  addr ? `${addr.slice(0, 6)}…${addr.slice(-6)}` : '—';

const formatWindowDate = (iso: string, lang: string) =>
  new Date(iso).toLocaleString(lang, { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatCountdown = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
};

const Withdrawals: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [win, setWindow] = useState<WithdrawalWindow | null>(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const [amount, setAmount] = useState('');
  const [wallet, setWallet] = useState('');
  const [savedWallet, setSavedWallet] = useState<string | null>(null);
  const [editingWallet, setEditingWallet] = useState(false);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletMsg, setWalletMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, win] = await Promise.all([api.getWithdrawals(), api.getWithdrawalWindow()]);
      const items: Withdrawal[] = list.items || list;
      setWithdrawals(items);
      setWindow(win);
      setSavedWallet(win.savedWallet);
      // No saved wallet yet: suggest the one used last time
      const lastWallet = items.find(w => w.walletAddress)?.walletAddress;
      if (!win.savedWallet && lastWallet) setWallet(prev => prev || lastWallet);
      if (!win.savedWallet) setEditingWallet(true);
    } catch (err) {
      console.error('Failed to load withdrawals:', err);
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  // Tick for the countdown; re-fetch window state when it flips open/closed
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const boundary = win ? new Date(win.open ? win.closesAt : win.opensAt).getTime() : null;
  useEffect(() => {
    if (boundary !== null && now >= boundary) load();
  }, [now, boundary, load]);

  const totalWithdrawn = withdrawals
    .filter(w => w.status === 'PAID')
    .reduce((sum, w) => sum + w.amount, 0);
  const pendingAmount = withdrawals
    .filter(w => w.status === 'PENDING')
    .reduce((sum, w) => sum + w.amount, 0);
  const available = win?.availableBalance ?? 0;

  const isOpen = !!win?.open && (boundary === null || now < boundary);
  const walletInputValid = TRX_ADDRESS_RE.test(wallet.trim());

  const handleSaveWallet = async (address: string | null) => {
    setWalletMsg(null);
    if (address !== null && !TRX_ADDRESS_RE.test(address.trim())) {
      setWalletMsg({ type: 'err', text: t('withdrawals.invalidWallet') });
      return;
    }
    setWalletBusy(true);
    try {
      const res = await api.saveWithdrawalWallet(address === null ? null : address.trim());
      setSavedWallet(res.savedWallet);
      setEditingWallet(!res.savedWallet);
      setWallet('');
      setWalletMsg({ type: 'ok', text: res.savedWallet ? t('withdrawals.walletSaved') : t('withdrawals.walletRemoved') });
    } catch (err: any) {
      setWalletMsg({ type: 'err', text: err.message });
    } finally {
      setWalletBusy(false);
    }
  };

  const startEditWallet = () => {
    setWallet(savedWallet || '');
    setEditingWallet(true);
    setWalletMsg(null);
  };
  const amountNum = parseFloat(amount);
  const amountValid = !isNaN(amountNum) && amountNum > 0 && amountNum <= available;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    if (!savedWallet) {
      setFormError(t('withdrawals.walletRequired'));
      return;
    }
    if (!amountValid) {
      setFormError(t('withdrawals.invalidAmount'));
      return;
    }

    setSubmitting(true);
    try {
      await api.createWithdrawal(Math.floor(amountNum * 100) / 100);
      setFormSuccess(t('withdrawals.requestSent'));
      setAmount('');
      await load();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div style={{ color: '#a1a1aa', padding: '2rem', textAlign: 'center' }}>{t('common.loading')}</div>;
  }

  return (
    <div className="withdrawals-section">
      {/* Summary Cards */}
      <div className="wd-stats">
        <div className="wd-stat-card">
          <div className="wd-stat-icon wd-stat-icon--available">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 12V7H5a2 2 0 010-4h14v4" />
              <path d="M3 5v14a2 2 0 002 2h16v-5" />
              <path d="M18 12a2 2 0 100 4h4v-4h-4z" />
            </svg>
          </div>
          <div className="wd-stat-info">
            <span className="wd-stat-label">{t('withdrawals.available')}</span>
            <span className="wd-stat-value">{formatCurrency(available)}</span>
          </div>
        </div>
        <div className="wd-stat-card">
          <div className="wd-stat-icon wd-stat-icon--pending">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="wd-stat-info">
            <span className="wd-stat-label">{t('withdrawals.pending')}</span>
            <span className="wd-stat-value">{formatCurrency(pendingAmount)}</span>
          </div>
        </div>
        <div className="wd-stat-card">
          <div className="wd-stat-icon wd-stat-icon--paid">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </div>
          <div className="wd-stat-info">
            <span className="wd-stat-label">{t('withdrawals.totalWithdrawn')}</span>
            <span className="wd-stat-value">{formatCurrency(totalWithdrawn)}</span>
          </div>
        </div>
      </div>

      {/* Withdrawal wallet setup (available any day) */}
      <div className={`wd-wallet-card ${savedWallet && !editingWallet ? '' : 'wd-wallet-card--edit'}`}>
        <div className="wd-wallet-head">
          <div className="wd-wallet-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12V7H5a2 2 0 010-4h14v4" />
              <path d="M3 5v14a2 2 0 002 2h16v-5" />
              <path d="M18 12a2 2 0 100 4h4v-4h-4z" />
            </svg>
          </div>
          <div className="wd-wallet-title">
            <h3>{t('withdrawals.walletTitle')}</h3>
            <span>{t('withdrawals.walletDesc')}</span>
          </div>
          {savedWallet && !editingWallet && (
            <span className="wd-saved-chip">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              {t('withdrawals.saved')}
            </span>
          )}
        </div>

        {savedWallet && !editingWallet ? (
          <div className="wd-saved-wallet">
            <span className="wd-network">TRC20</span>
            <span className="wd-saved-address mono" title={savedWallet}>{savedWallet}</span>
            <div className="wd-saved-actions">
              <button type="button" className="wd-link" onClick={startEditWallet} disabled={walletBusy}>{t('withdrawals.changeWallet')}</button>
              <button type="button" className="wd-link wd-link--danger" onClick={() => handleSaveWallet(null)} disabled={walletBusy}>{t('withdrawals.removeWallet')}</button>
            </div>
          </div>
        ) : (
          <form className="wd-wallet-form" onSubmit={e => { e.preventDefault(); handleSaveWallet(wallet); }}>
            <div className="wd-input-wrap">
              <span className="wd-network">TRC20</span>
              <input
                type="text"
                value={wallet}
                onChange={e => setWallet(e.target.value.trim())}
                placeholder={t('withdrawals.walletPlaceholder')}
                spellCheck={false}
                autoComplete="off"
                disabled={walletBusy}
                className="mono"
                aria-label={t('withdrawals.walletLabel')}
              />
            </div>
            <button type="submit" className="wd-wallet-save" disabled={walletBusy || !walletInputValid}>
              {walletBusy ? <span className="wd-spinner" /> : t('withdrawals.saveWallet')}
            </button>
            {savedWallet && (
              <button type="button" className="wd-wallet-cancel" onClick={() => { setEditingWallet(false); setWallet(''); setWalletMsg(null); }} disabled={walletBusy}>
                {t('withdrawals.cancel')}
              </button>
            )}
          </form>
        )}
        {wallet && !walletInputValid && editingWallet && <small className="wd-hint wd-hint--err">{t('withdrawals.invalidWallet')}</small>}
        {walletMsg && <small className={`wd-hint ${walletMsg.type === 'err' ? 'wd-hint--err' : 'wd-hint--ok'}`}>{walletMsg.text}</small>}
      </div>

      {/* Window status + request form */}
      <div className="wd-request">
        <div className={`wd-window ${isOpen ? 'wd-window--open' : 'wd-window--closed'}`}>
          <div className="wd-window-head">
            <span className="wd-window-badge">
              <span className="wd-window-dot" />
              {isOpen ? t('withdrawals.windowOpen') : t('withdrawals.windowClosed')}
            </span>
            <span className="wd-window-schedule">{t('withdrawals.schedule')}</span>
          </div>
          {win && (
            <div className="wd-window-body">
              <span className="wd-window-label">
                {isOpen ? t('withdrawals.closesIn') : t('withdrawals.opensIn')}
              </span>
              <span className="wd-window-countdown">{boundary !== null ? formatCountdown(boundary - now) : '—'}</span>
              <span className="wd-window-date">
                {formatWindowDate(isOpen ? win.closesAt : win.opensAt, i18n.language)}
              </span>
            </div>
          )}
          <p className="wd-window-note">{t('withdrawals.scheduleNote')}</p>
        </div>

        <form className="wd-form" onSubmit={handleSubmit}>
          <h3>{t('withdrawals.requestTitle')}</h3>

          <div className="wd-field">
            <span>{t('withdrawals.payTo')}</span>
            {savedWallet ? (
              <div className="wd-payto">
                <span className="wd-network">TRC20</span>
                <span className="wd-saved-address mono" title={savedWallet}>{savedWallet}</span>
              </div>
            ) : (
              <div className="wd-payto wd-payto--empty">{t('withdrawals.walletRequired')}</div>
            )}
          </div>

          <label className="wd-field">
            <span>{t('withdrawals.amountLabel')}</span>
            <div className="wd-input-wrap">
              <span className="wd-network">USD</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
                disabled={!isOpen || submitting}
              />
              <button
                type="button"
                className="wd-max"
                onClick={() => setAmount((Math.floor(available * 100) / 100).toFixed(2))}
                disabled={!isOpen || submitting || available <= 0}
              >
                {t('withdrawals.max')}
              </button>
            </div>
            <small className="wd-hint">{t('withdrawals.availableHint', { amount: formatCurrency(available) })}</small>
          </label>

          {formError && <div className="wd-alert wd-alert--err">{formError}</div>}
          {formSuccess && <div className="wd-alert wd-alert--ok">{formSuccess}</div>}

          <button
            type="submit"
            className="wd-submit"
            disabled={!isOpen || submitting || !savedWallet || !amountValid}
          >
            {submitting ? <span className="wd-spinner" /> : isOpen ? t('withdrawals.submit') : t('withdrawals.submitClosed')}
          </button>
          <p className="wd-form-note">{t('withdrawals.reviewNote')}</p>
        </form>
      </div>

      {/* Withdrawal Table */}
      <div className="wd-table-wrap">
        <div className="wd-table-header">
          <h3>{t('withdrawals.historyTitle')}</h3>
          <span className="wd-table-count">{t('withdrawals.count', { count: withdrawals.length })}</span>
        </div>
        {withdrawals.length === 0 ? (
          <div style={{ color: '#a1a1aa', padding: '2rem', textAlign: 'center' }}>{t('withdrawals.noWithdrawals')}</div>
        ) : (
          <div className="wd-table-scroll">
            <table className="wd-table">
              <thead>
                <tr>
                  <th>{t('withdrawals.requestedOn')}</th>
                  <th>{t('withdrawals.amount')}</th>
                  <th>{t('withdrawals.wallet')}</th>
                  <th>{t('withdrawals.status')}</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.map(w => (
                  <tr key={w.id} className={w.status === 'PENDING' ? 'wd-row-pending' : ''}>
                    <td>
                      <div className="wd-week">
                        <span className="wd-week-date">
                          {new Date(w.createdAt).toLocaleDateString(i18n.language, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        <span className="wd-week-label">
                          {new Date(w.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </td>
                    <td className="mono wd-payout">{formatCurrency(w.amount)}</td>
                    <td className="mono" title={w.walletAddress || undefined}>{shortWallet(w.walletAddress)}</td>
                    <td>
                      <span className={`wd-status wd-status--${w.status.toLowerCase()}`}>
                        <span className="wd-status-dot" />
                        {t(`withdrawals.status${w.status.charAt(0)}${w.status.slice(1).toLowerCase()}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Withdrawals;
